import { useDrag, useGesture } from '@use-gesture/react';
import { clamp, range } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { DAYS, HOURS, MINUTES, SECONDS, type Tuple2 } from 'src/lib/player/constants';
import type { TimeMode } from 'src/lib/player/store';
import {
  NBSP,
  ceilTime,
  ceilTimeAbsolute,
  floorTime,
  floorTimeAbsolute,
  leftpad2,
} from 'src/lib/player/utils';

type StrideDefinition = { primary: number; secondary: number };

const HANDLE_PADDING = 2 * 9;
const TICK_WIDTH = 100;
const STRIDES: Array<StrideDefinition> = [
  { primary: 30 * SECONDS, secondary: 5 * SECONDS },
  { primary: 1 * MINUTES, secondary: 15 * SECONDS },
  { primary: 5 * MINUTES, secondary: 1 * MINUTES },
  { primary: 10 * MINUTES, secondary: 2 * MINUTES },
  { primary: 15 * MINUTES, secondary: 5 * MINUTES },
  { primary: 30 * MINUTES, secondary: 5 * MINUTES },
  { primary: 1 * HOURS, secondary: 15 * MINUTES },
  { primary: 2 * HOURS, secondary: 30 * MINUTES },
  { primary: 6 * HOURS, secondary: 1 * HOURS },
  { primary: 12 * HOURS, secondary: 2 * HOURS },
  { primary: 1 * DAYS, secondary: 6 * HOURS },
  { primary: 3 * DAYS, secondary: 12 * HOURS },
  { primary: 7 * DAYS, secondary: 1 * DAYS },
  { primary: 14 * DAYS, secondary: 3 * DAYS },
  { primary: 28 * DAYS, secondary: 7 * DAYS },
  { primary: 70 * DAYS, secondary: 14 * DAYS },
  { primary: 140 * DAYS, secondary: 35 * DAYS },
  { primary: 365 * DAYS, secondary: 73 * DAYS },
];

function findGranularities(sliderDuration: number, stride: number): [number, number, number] {
  let maxGranularity = SECONDS;
  if (sliderDuration >= MINUTES) {
    maxGranularity = MINUTES;
  }
  if (sliderDuration >= HOURS) {
    maxGranularity = HOURS;
  }
  if (sliderDuration >= DAYS) {
    maxGranularity = DAYS;
  }

  let minGranularity = SECONDS;
  if (stride >= MINUTES) {
    minGranularity = MINUTES;
  }
  if (stride >= HOURS) {
    minGranularity = HOURS;
  }
  if (stride >= DAYS) {
    minGranularity = DAYS;
  }

  let nextGranularity = SECONDS;
  if (minGranularity === SECONDS) {
    nextGranularity = MINUTES;
  }
  if (minGranularity === MINUTES) {
    nextGranularity = HOURS;
  }
  if (minGranularity === HOURS) {
    nextGranularity = DAYS;
  }
  return [minGranularity, nextGranularity, maxGranularity];
}

function findTicksRelative(
  visibleMin: number,
  visibleMax: number,
  sliderMin: number,
  sliderMax: number,
  count: number,
  tickStrides: Array<StrideDefinition>,
): {
  sticky: Array<{ value: number; label: string }>;
  primary: Array<{ value: number; label: string }>;
  secondary: Array<number>;
  granularity: [number, number];
} {
  let stride = tickStrides[0];
  for (const _stride of tickStrides) {
    if ((visibleMax - visibleMin) / _stride.primary < count) {
      stride = _stride;
      break;
    }
  }

  const ticksPrimaryValues = range(
    floorTime(visibleMin - sliderMin, stride.primary),
    ceilTime(visibleMax - sliderMin, stride.primary) + 1,
    stride.primary,
  ).map((t) => t + sliderMin);
  const ticksSecondaryValues = range(
    floorTime(visibleMin - sliderMin, stride.secondary),
    ceilTime(visibleMax - sliderMin, stride.secondary) + 1,
    stride.secondary,
  )
    .map((t) => t + sliderMin)
    .filter((tick) => !ticksPrimaryValues.includes(tick));

  const [minGranularity, nextGranularity, maxGranularity] = findGranularities(
    sliderMax - sliderMin,
    stride.primary,
  );

  const ticksSticky =
    minGranularity === maxGranularity
      ? ticksPrimaryValues.map((t) => ({
          value: t,
          label: formatStickyTickLabelRelative(t - sliderMin, minGranularity, maxGranularity),
        }))
      : range(
          floorTime(visibleMin - sliderMin, nextGranularity),
          ceilTime(visibleMax - sliderMin, nextGranularity) + 1,
          nextGranularity,
        ).map((t) => ({
          value: t + sliderMin,
          label: formatStickyTickLabelRelative(t, minGranularity + 1, maxGranularity),
        }));

  return {
    sticky: ticksSticky,
    primary:
      minGranularity === maxGranularity
        ? []
        : ticksPrimaryValues.map((t) => ({
            value: t,
            label: formatTickLabelRelative(t - sliderMin, minGranularity),
          })),
    secondary: ticksSecondaryValues,
    granularity: [minGranularity, maxGranularity],
  };
}

function findTicksAbsolute(
  visibleMin: number,
  visibleMax: number,
  sliderMin: number,
  sliderMax: number,
  count: number,
  tickStrides: Array<StrideDefinition>,
): {
  sticky: Array<{ value: number; label: string }>;
  primary: Array<{ value: number; label: string }>;
  secondary: Array<number>;
  granularity: [number, number];
} {
  let stride = tickStrides[0];
  for (const _stride of tickStrides) {
    if ((visibleMax - visibleMin) / _stride.primary < count) {
      stride = _stride;
      break;
    }
  }

  const sliderFloorTime = floorTimeAbsolute(sliderMin, stride.primary);
  const sliderCeilTime = ceilTimeAbsolute(sliderMax, stride.primary);

  const visibleFloorTime =
    sliderFloorTime + Math.floor((visibleMin - sliderFloorTime) / stride.primary) * stride.primary;
  const visibleCeilTime =
    sliderCeilTime - Math.floor((sliderCeilTime - visibleMax) / stride.primary) * stride.primary;

  const ticksPrimaryValues = range(visibleFloorTime, visibleCeilTime + 1, stride.primary);
  const ticksSecondaryValues = range(
    visibleFloorTime,
    visibleCeilTime + 1,
    stride.secondary,
  ).filter((tick) => !ticksPrimaryValues.includes(tick));

  const [minGranularity, nextGranularity, maxGranularity] = findGranularities(
    sliderMax - sliderMin,
    stride.primary,
  );

  const ticksSticky =
    minGranularity === maxGranularity
      ? ticksPrimaryValues.map((t) => ({
          value: t,
          label: formatStickyTickLabelAbsolute(t, minGranularity),
        }))
      : range(
          floorTimeAbsolute(visibleMin, nextGranularity),
          ceilTimeAbsolute(visibleMax, nextGranularity),
          nextGranularity,
        ).map((t) => ({
          value: t,
          label: formatStickyTickLabelAbsolute(t, minGranularity + 1),
        }));

  return {
    sticky: ticksSticky,
    primary:
      minGranularity === maxGranularity
        ? []
        : ticksPrimaryValues.map((t) => ({
            value: t,
            label: formatTickLabelAbsolute(t, minGranularity),
          })),
    secondary: ticksSecondaryValues,
    granularity: [minGranularity, maxGranularity],
  };
}

function formatHandleLabelAbsolute(time: number): JSX.Element {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const min = date.getMinutes();
  const hour = date.getHours();
  const sec = date.getSeconds();

  return (
    <>
      {year}-{leftpad2(month)}-{leftpad2(day)}
      <br />
      {leftpad2(hour)}h{NBSP}
      {leftpad2(min)}m{NBSP}
      {leftpad2(sec)}s
    </>
  );
}

function formatHandleLabelRelative(time: number, showDays: boolean): string {
  const sec = Math.floor(time / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  return showDays
    ? `${leftpad2(day)}d${NBSP}${leftpad2(hour % 24)}h${NBSP}${leftpad2(
        min % 60,
      )}m${NBSP}${leftpad2(sec % 60)}s`
    : `${leftpad2(hour)}h${NBSP}${leftpad2(min % 60)}m${NBSP}${leftpad2(sec % 60)}s`;
}

function formatTickLabelRelative(time: number, granularity: number): string {
  const seconds = Math.floor(time / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  switch (granularity) {
    case SECONDS:
      return `${leftpad2(seconds % 60)}s`;
    case MINUTES:
      return `${leftpad2(minutes % 60)}m`;
    case HOURS:
      return `${leftpad2(hours % 24)}h`;
    case DAYS:
      return `${leftpad2(days)}d`;
    default:
      return '-';
  }
}

function formatStickyTickLabelRelative(
  time: number,
  minGranularity: number,
  maxGranularity: number,
): string {
  const seconds = Math.floor(time / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const output: Array<string> = [];
  if (minGranularity <= DAYS && DAYS <= maxGranularity) {
    output.push(`${leftpad2(days)}d`);
  }
  if (minGranularity <= HOURS && HOURS <= maxGranularity) {
    output.push(`${leftpad2(hours % 24)}h`);
  }
  if (minGranularity <= MINUTES && MINUTES <= maxGranularity) {
    output.push(`${leftpad2(minutes % 60)}m`);
  }
  if (minGranularity <= SECONDS && SECONDS <= maxGranularity) {
    output.push(`${leftpad2(seconds % 60)}s`);
  }
  return output.join(' ');
}

function formatTickLabelAbsolute(time: number, granularity: number): string {
  const date = new Date(time);

  switch (granularity) {
    case SECONDS:
      return `${leftpad2(date.getSeconds())}s`;
    case MINUTES:
      return `${leftpad2(date.getMinutes())}m`;
    case HOURS:
      return `${leftpad2(date.getHours())}h`;
    case DAYS:
      return leftpad2(date.getDate());
    default:
      return '-';
  }
}

function formatStickyTickLabelAbsolute(time: number, minGranularity: number): string {
  const date = new Date(time);
  const output: Array<string> = [];
  if (minGranularity <= DAYS) {
    output.push(
      `${date.getFullYear()}-${leftpad2(date.getMonth() + 1)}-${leftpad2(date.getDate())}`,
    );
  }
  if (minGranularity <= HOURS) {
    output.push(`${leftpad2(date.getHours())}h`);
  }
  if (minGranularity <= MINUTES) {
    output.push(`${leftpad2(date.getMinutes())}m`);
  }
  if (minGranularity <= SECONDS) {
    output.push(`${leftpad2(date.getSeconds())}s`);
  }
  return output.join(' ');
}

function calcMinZoom(timeDuration: number, min: number, max: number): number {
  return Math.log(-timeDuration / (min - max)) / Math.log(2);
}

export type SliderProps = {
  value: number;
  min: number;
  max: number;
  timeMode: TimeMode;
  shade?: Array<Tuple2>;
  onChange: (newValue: number) => void;
};

export default function Slider({
  min,
  max,
  value,
  timeMode,
  shade = [],
  onChange,
}: SliderProps): JSX.Element {
  const [zoom, setZoom] = useState(calcMinZoom(30 * MINUTES, min, max));
  const expZoom = 1 / 2 ** zoom;

  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderSliderRef = useRef<HTMLDivElement>(null);
  const sliderHandleRef = useRef<HTMLDivElement>(null);
  const sliderTickRef = useRef<HTMLDivElement>(null);

  const visibleRangeStart = min + (value - min) * ((expZoom - 1) / expZoom);
  const visibleRangeEnd = visibleRangeStart + (max - min) / expZoom;
  const sliderWidth = sliderRef.current?.offsetWidth ?? 0;
  const handleWidth = sliderHandleRef.current?.offsetWidth ?? 0;
  const optimalTickCount = sliderWidth / TICK_WIDTH;
  const {
    primary: ticksPrimary,
    secondary: ticksSecondary,
    sticky: ticksSticky,
    granularity: [, tickMaxGranularity],
  } = timeMode === 'RELATIVE'
    ? findTicksRelative(visibleRangeStart, visibleRangeEnd, min, max, optimalTickCount, STRIDES)
    : findTicksAbsolute(visibleRangeStart, visibleRangeEnd, min, max, optimalTickCount, STRIDES);

  useGesture(
    {
      onDrag: ({ pinching, down, delta: [deltaX], cancel }) => {
        if (pinching) {
          cancel();
          return;
        }
        if (down) {
          if (sliderRef.current == null) return;

          onChange(
            clamp(
              value - (deltaX / (sliderRef.current.offsetWidth * expZoom)) * (max - min),
              min,
              max,
            ),
          );
        }
      },
      onPinch: ({ delta: [deltaX], offset: [offsetX] }) => {
        const minZoom = calcMinZoom(10 * MINUTES, min, max);
        setZoom((zoom) => clamp(zoom - deltaX / offsetX, minZoom, 0));
      },
      onWheel: ({ ctrlKey, delta: [, deltaY] }) => {
        if (!ctrlKey) {
          const minZoom = calcMinZoom(10 * MINUTES, min, max);
          setZoom((zoom) => clamp(zoom + deltaY / 1000, minZoom, 0));
        }
      },
    },
    { target: sliderTickRef },
  );
  useDrag(
    ({ down, delta: [deltaX] }) => {
      if (down) {
        onChange(
          clamp(
            value + (deltaX / (sliderWidth - (handleWidth - HANDLE_PADDING))) * (max - min),
            min,
            max,
          ),
        );
      }
    },
    { target: sliderSliderRef },
  );

  useEffect(() => {
    if (sliderRef.current == null) return;
    setZoom(calcMinZoom(clamp(sliderRef.current.offsetWidth / 10, 30, 120) * MINUTES, min, max));
  }, []);

  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    const slider = sliderRef.current;
    if (slider == null) return;

    slider.addEventListener('touchstart', handler);
    return () => {
      slider.removeEventListener('touchstart', handler);
    };
  }, []);

  function calcOffsetFraction(v: number) {
    return (v - min) / (max - min);
  }
  function calcZoomedOffsetFraction(v: number) {
    return (expZoom * v - expZoom * value + value - min) / (max - min);
  }

  return (
    <div className="slider" ref={sliderRef}>
      <div className="slider-tick-wrapper" ref={sliderTickRef}>
        {ticksSticky.map((tick, i) => (
          <div
            key={`sticky${tick.value}`}
            className="slider-tick-sticky"
            style={{
              transform: `translateX(${clamp(
                calcZoomedOffsetFraction(tick.value) * sliderWidth,
                0,
                sliderWidth - 1,
              )}px)`,
              zIndex: 200 + i,
            }}
          >
            <span>{tick.label}</span>
          </div>
        ))}
        {ticksPrimary.map((tick, i) => (
          <div
            key={`primary${tick.value}`}
            className="slider-tick-primary"
            style={{
              zIndex: 100 + i,
              transform: `translateX(${clamp(
                calcZoomedOffsetFraction(tick.value) * sliderWidth,
                Number.NEGATIVE_INFINITY,
                sliderWidth,
              )}px)`,
            }}
          >
            <span>{tick.label}</span>
          </div>
        ))}
        {ticksSecondary.map((tick) => (
          <div
            key={`secondary${tick}`}
            className="slider-tick-secondary"
            style={{
              transform: `translateX(${clamp(
                calcZoomedOffsetFraction(tick) * sliderWidth,
                0,
                sliderWidth,
              )}px)`,
            }}
          />
        ))}
      </div>
      <div className="slider-slider" ref={sliderSliderRef}>
        <div
          className="slider-handle"
          ref={sliderHandleRef}
          style={{
            width: tickMaxGranularity === DAYS && timeMode === 'RELATIVE' ? 90 : undefined,
            transform: `translateX(${
              ((value - min) / (max - min)) * (sliderWidth - (handleWidth - HANDLE_PADDING))
            }px)`,
          }}
        >
          <div
            className="slider-handle-arrow"
            style={{
              transform: `translateX(${
                ((value - min) / (max - min)) * (handleWidth - HANDLE_PADDING)
              }px)`,
            }}
          />
          {timeMode === 'ABSOLUTE'
            ? formatHandleLabelAbsolute(value)
            : formatHandleLabelRelative(value - min, tickMaxGranularity === DAYS)}
        </div>
        <div
          className="slider-track"
          style={{
            left: 0,
            width: calcOffsetFraction(value) * sliderWidth,
          }}
        />
        {shade.map(([shadeMin, shadeMax]) => (
          <div
            key={`shade-${shadeMin}-${shadeMax}`}
            className="slider-shade"
            style={{
              left: calcOffsetFraction(shadeMin) * sliderWidth,
              width: (calcOffsetFraction(shadeMax) - calcOffsetFraction(shadeMin)) * sliderWidth,
            }}
          />
        ))}
        <div className="slider-background" />
      </div>
    </div>
  );
}
